import { Program } from '@playbook-technologies/common-enums';
import { ApiAdminProgramUpsertRequest } from '@client/new/api/types';
import {
  DEFAULT_COLOR,
  DEFAULT_CONTENT_STRUCTURE,
  DEFAULT_DURATION_LENGTH,
  DEFAULT_DURATION_UNIT,
  DEFAULT_PUBLISHING_BEHAVIOUR_CATEGORY,
  DEFAULT_PUBLISHING_BEHAVIOUR_PROGRAM,
  FormMedia,
  FormReview,
  ProgramFormValues,
  PUBLISHING_BEHAVIOUR_OPTIONS,
  STRUCTURE_OPTIONS,
  StructureChoice,
  TARGET_AREA_OPTIONS,
} from './programForm.types';

// Generated `ApiAdminProgramUpsertRequest` mistypes enum columns as `object`,
// `duration` as `Array<string>`, and media as strict `ApiMediaResponse` (real
// shape is the looser `CollectionMedia` w/ all fields optional). This boundary
// type matches what the backend actually accepts.
export type ApiTestimonial = {
  name: string;
  text: string;
  media: FormMedia;
};

export type ApiFaq = {
  question: string;
  answer: string;
};

export type ApiHiddenSections = {
  testimonials?: boolean;
  faq?: boolean;
};

export type ProgramUpsertRequest = Omit<
  ApiAdminProgramUpsertRequest,
  | 'inAppPurchase'
  | 'contentStructure'
  | 'accessType'
  | 'publishingBehaviour'
  | 'duration'
  | 'mediaCover'
  | 'mediaIntro'
  | 'testimonials'
  | 'hiddenSections'
  | 'faq'
> & {
  inAppPurchase?: string;
  contentStructure?: string;
  accessType?: string;
  publishingBehaviour?: string;
  duration?: [number, string];
  mediaCover?: FormMedia;
  mediaIntro?: FormMedia;
  testimonials?: ApiTestimonial[];
  hiddenSections?: ApiHiddenSections;
  faq?: ApiFaq[];
  swapsEnabled?: boolean;
};

type ProgramEntityForForm = {
  name?: string;
  type: number;
  goal?: string;
  description?: string;
  mediaCover?: FormMedia;
  mediaIntro?: FormMedia;
  color?: string;
  tags?: string[] | null;
  requiredEquipment?: string[] | null;
  location?: string | null;
  difficulty?: string | null;
  contentStructure?: string | null;
  publishingBehaviour?: string | null;
  workoutLimit?: number | null;
  duration?: [number, string] | null;
  daysPerWeek?: number | null;
  sessionDuration?: string | null;
  testimonials?: ApiTestimonial[] | null;
  hiddenSections?: ApiHiddenSections | null;
  faq?: ApiFaq[] | null;
  inAppPurchase?: string | null;
};

const isCategoryEntity = (type: number): boolean =>
  type === Program.Type.Collection.value;

const structureToTypeEnum = (structure: StructureChoice): number =>
  structure === 'category'
    ? Program.Type.Collection.value
    : Program.Type.Program.value;

const STRUCTURE_VALUES = new Set<string>(STRUCTURE_OPTIONS.map((o) => o.value));

const isStructureChoice = (value: string): value is StructureChoice =>
  STRUCTURE_VALUES.has(value);

const PUBLISHING_BEHAVIOUR_VALUES = new Set<string>(
  PUBLISHING_BEHAVIOUR_OPTIONS.map((o) => o.value),
);

// Legacy rows may carry values outside the current option set. The MUI select
// would render those as blank and silently round-trip the garbage on save;
// snap unknown values to the type-specific default instead.
const normalizePublishingBehaviour = (
  raw: string | null | undefined,
  isCategory: boolean,
): string => {
  if (raw && PUBLISHING_BEHAVIOUR_VALUES.has(raw)) return raw;
  return isCategory
    ? DEFAULT_PUBLISHING_BEHAVIOUR_CATEGORY
    : DEFAULT_PUBLISHING_BEHAVIOUR_PROGRAM;
};

// Backend writes NOT NULL enum columns from the request body with no defaults,
// so `{ name, type, influencerId }` alone fails the INSERT. `accessType` is
// intentionally omitted: the modern collection.model strips it for
// unifiedProgramsEnabled users, which is the only audience of this drawer.
const CREATE_DEFAULTS: Partial<ProgramUpsertRequest> = {
  isVisible: false,
};

const TARGET_AREA_SET = new Set<string>(TARGET_AREA_OPTIONS);

// Color is stored without a leading `#` (legacy invariant: `color: hex.slice(1)`).
// Strip on read so entities that arrive with `#` still pass the schema's hex
// regex. Expand 3-digit shorthand (`fff`) to 6 so legacy rows don't block save.
const normalizeColor = (color: string | undefined): string => {
  if (!color) return DEFAULT_COLOR;
  const stripped = color.startsWith('#') ? color.slice(1) : color;
  if (/^[0-9a-fA-F]{3}$/.test(stripped)) {
    return stripped
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return stripped;
};

const toContentStructure = (
  isCategory: boolean,
  raw: string | null | undefined,
): StructureChoice => {
  if (isCategory) return 'category';
  if (raw && isStructureChoice(raw) && raw !== 'category') return raw;
  return DEFAULT_CONTENT_STRUCTURE;
};

export const programToFormValues = (
  program: ProgramEntityForForm,
): ProgramFormValues => {
  const isCategory = isCategoryEntity(program.type);
  const [length, unit] = program.duration ?? [];
  const allTags = program.tags ?? [];
  return {
    name: program.name ?? '',
    goal: program.goal ?? '',
    description: program.description ?? '',
    mediaCover: program.mediaCover ?? null,
    mediaIntro: program.mediaIntro ?? null,
    color: normalizeColor(program.color),
    tags: allTags.filter((t) => !TARGET_AREA_SET.has(t)),
    targetArea: allTags.filter((t) => TARGET_AREA_SET.has(t)),
    requiredEquipment: program.requiredEquipment ?? [],
    location: program.location ?? '',
    difficulty: program.difficulty ?? '',
    contentStructure: toContentStructure(isCategory, program.contentStructure),
    publishingBehaviour: normalizePublishingBehaviour(
      program.publishingBehaviour,
      isCategory,
    ),
    workoutLimit:
      program.workoutLimit != null ? String(program.workoutLimit) : '',
    durationLength: length != null ? String(length) : DEFAULT_DURATION_LENGTH,
    durationUnit: unit ?? DEFAULT_DURATION_UNIT,
    daysPerWeek: program.daysPerWeek != null ? String(program.daysPerWeek) : '',
    sessionDuration: program.sessionDuration ?? '',
    // Defaults mirror legacy `getMappedCollection`: testimonials are hidden
    // by default, FAQ is visible by default. The asymmetry is intentional.
    // For legacy rows missing `hiddenSections`, the first Save will persist
    // these implicit defaults explicitly (`hiddenSections.faq = false`,
    // `hiddenSections.testimonials = true`) — a benign implicit-to-explicit
    // migration that does not change what the consumer app shows.
    reviewsEnabled: !(program.hiddenSections?.testimonials ?? true),
    reviews: (program.testimonials ?? []).map((t) => ({
      name: t.name,
      text: t.text,
      media: t.media ?? null,
    })),
    faqsEnabled: !(program.hiddenSections?.faq ?? false),
    faqs: (program.faq ?? []).map((f) => ({
      question: f.question,
      answer: f.answer,
    })),
    inAppPurchaseAllowed: program.inAppPurchase !== 'hide',
  };
};

// Visibility is driven by `hiddenSections`; the actual list is always sent
// as-is so toggling a section off does not erase server-side data.
const reviewsFields = (values: ProgramFormValues) => ({
  testimonials: values.reviews.map((r) => ({
    name: r.name,
    text: r.text,
    media: r.media,
  })),
});

const faqsFields = (values: ProgramFormValues) => ({
  faq: values.faqs.map((f) => ({ question: f.question, answer: f.answer })),
});

const buildHiddenSections = (values: ProgramFormValues): ApiHiddenSections => ({
  testimonials: !values.reviewsEnabled,
  faq: !values.faqsEnabled,
});

// Backend stores Category tags + Target Area items in the same `tags` column;
// no separate `targetArea` column exists. Empty optional fields are omitted
// to mirror the legacy payload exactly.
const advancedFields = (values: ProgramFormValues) => ({
  tags: Array.from(new Set([...values.tags, ...values.targetArea])),
  ...(values.requiredEquipment.length > 0
    ? { requiredEquipment: values.requiredEquipment }
    : {}),
  ...(values.location ? { location: values.location } : {}),
  ...(values.difficulty ? { difficulty: values.difficulty } : {}),
});

// Backend columns are non-negative integers (workoutLimit, daysPerWeek,
// duration length). Reject negatives and non-integers so a paste or
// pre-filled value cannot slip past the `type="number"` UI guard.
const parseOptionalInt = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0) return undefined;
  return parsed;
};

const parseDuration = (
  length: string,
  unit: string,
): [number, string] | undefined => {
  const n = parseOptionalInt(length);
  if (n === undefined) return undefined;
  return [n, unit || DEFAULT_DURATION_UNIT];
};

const programOnlyDurationFields = (values: ProgramFormValues) => {
  const duration = parseDuration(values.durationLength, values.durationUnit);
  const daysPerWeek = parseOptionalInt(values.daysPerWeek);
  const sessionDuration = values.sessionDuration || undefined;
  return {
    ...(duration ? { duration } : {}),
    ...(daysPerWeek != null ? { daysPerWeek } : {}),
    ...(sessionDuration ? { sessionDuration } : {}),
  };
};

const workoutLimitField = (raw: string) => {
  const workoutLimit = parseOptionalInt(raw);
  return workoutLimit != null ? { workoutLimit } : {};
};

const inAppPurchaseField = (allowed: boolean) => ({
  inAppPurchase: allowed ? 'allow' : 'hide',
});

const buildBasePayload = (
  values: ProgramFormValues,
  ctx: { creatorId: number },
) => ({
  influencerId: ctx.creatorId,
  name: values.name,
  type: structureToTypeEnum(values.contentStructure),
  description: values.description,
  color: values.color,
  mediaCover: values.mediaCover,
  mediaIntro: values.mediaIntro,
  publishingBehaviour: values.publishingBehaviour,
  goal: values.goal,
  ...advancedFields(values),
  ...reviewsFields(values),
  ...faqsFields(values),
  hiddenSections: buildHiddenSections(values),
  // Only ride the save when the toggle actually loaded - an absent field
  // leaves the engine untouched, so a hidden or unavailable toggle can never
  // overwrite the stored program config.
  ...(typeof values.swapsEnabled === 'boolean'
    ? { swapsEnabled: values.swapsEnabled }
    : {}),
});

const buildTypeSpecificFields = (values: ProgramFormValues) =>
  values.contentStructure === 'category'
    ? workoutLimitField(values.workoutLimit)
    : {
        contentStructure: values.contentStructure,
        ...inAppPurchaseField(values.inAppPurchaseAllowed),
        ...programOnlyDurationFields(values),
      };

export const formValuesToCreatePayload = (
  values: ProgramFormValues,
  ctx: { creatorId: number },
): ProgramUpsertRequest => ({
  ...CREATE_DEFAULTS,
  ...buildBasePayload(values, ctx),
  ...buildTypeSpecificFields(values),
});

export const formValuesToEditPayload = (
  values: ProgramFormValues,
  ctx: { creatorId: number; programId: number },
): ProgramUpsertRequest => ({
  id: ctx.programId,
  ...buildBasePayload(values, { creatorId: ctx.creatorId }),
  ...buildTypeSpecificFields(values),
});
